/** A ProvisionalReason's `message` is Czech (SoraProvisionalReasonCodes-driven backend text);
 * `message_en` is the English equivalent, added 2026-09-16 after live user testing found every
 * reason staying Czech-only under the English UI language. Falls back to the Czech message when
 * `message_en` is missing (a future backend-added reason that hasn't been given an English
 * variant yet, rather than showing nothing). */
export function localizeReasonMessage(reason, language) {
  if (language === 'cs') return reason.message;
  return reason.message_en || reason.message;
}

/** Reason codes that never warrant the alarming warning-triangle badge next to a SAIL number,
 * even though the result is still provisional_result=true - a permanent, non-actionable
 * characteristic of the calculation method itself, not something wrong or missing. Today just
 * manual_footprint (population density in this app is always assessed from manually entered/
 * checked map points, never computed from the flight-area footprint - see decisions.md - so the
 * triangle would never go away and stops being a useful signal). The "Předběžný odhad" info box
 * still explains this normally regardless; only the small badge-level triangle is suppressed.
 * 2026-09-16, explicit user request. An accepted ARC-a declaration (atypical_declaration_accepted)
 * is likewise informational — the authority assesses it, the ARC notice shows it grey with an info
 * icon — so it does not light the triangle either. */
const NON_WARNING_REASON_CODES = new Set(['manual_footprint', 'atypical_declaration_accepted']);

/** Whether `reasons` (a mission/GrcAssessment's provisional_reasons array) contains at least one
 * reason that DOES warrant the SAIL-badge warning triangle - i.e. is not exclusively made up of
 * NON_WARNING_REASON_CODES entries. */
export function hasSailWarningReason(reasons) {
  return (reasons || []).some(r => !NON_WARNING_REASON_CODES.has(r.code));
}

/** Tooltip text for the SAIL-badge warning triangle - the localized message(s) of whichever
 * reason(s) actually made hasSailWarningReason true (manual_footprint excluded, same as the
 * triangle's own visibility), joined for the (rare) case of more than one. Empty string when there
 * is nothing warning-worthy - callers should already be gating the triangle itself on
 * hasSailWarningReason, so this only runs when there's something to show. 2026-09-16, user request:
 * hovering the triangle should explain WHY, not just that something is provisional. */
export function sailWarningTooltip(reasons, language) {
  return (reasons || [])
    .filter(r => !NON_WARNING_REASON_CODES.has(r.code))
    .map(r => localizeReasonMessage(r, language))
    .join(' ');
}
